<template>
    <div>
        <div class="relative">
            <button
                type="button"
                ref="buttonRef"
                class="btn-base btn-white min-h-10 flex items-center gap-2.5"
                :class="{ '!border-0 !shadow-none': !showBorder }"
                @click="showMore = !showMore"
                v-click-outside="() => (showMore = false)"
                id="hs-redemption-more-actions"
            >
                <Icon icon="dots-vertical" />
                <span v-if="showLabel">More</span>
            </button>
            <transition name="fade">
                <div
                    ref="popoverRef"
                    :style="floatingStyles"
                    class="z-30"
                    v-if="showMore"
                >
                    <div class="bg-white dark:bg-slate-800 p-3 rounded-lg shadow">
                        <ul class="text-sm">
                            <li>
                                <button
                                    class="btn-base btn-link px-2 py-1 text-left flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 w-full rounded"
                                    @click="showRedemption()"
                                    id="hs-redemption-view-action"
                                >
                                    <Icon
                                        icon="eye-on"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>View</span>
                                </button>
                            </li>
                            <li>
                                <button
                                    class="btn-base btn-link px-2 py-1 text-left flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 w-full rounded"
                                    @click="editRedemption()"
                                    id="hs-redemption-edit-action"
                                >
                                    <Icon
                                        icon="edit"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Edit</span>
                                </button>
                            </li>
                            <li v-if="showShare">
                                <button
                                    class="btn-base btn-link px-2 py-1 text-left flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 w-full rounded"
                                    id="hs-redemption-share-action"
                                >
                                    <Icon
                                        icon="share"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Share</span>
                                </button>
                            </li>
                            <li>
                                <button
                                    class="btn-base btn-link px-2 py-1 text-left flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 w-full rounded"
                                    @click="showDeleteConfirmation = true"
                                    id="hs-redemption-delete-action"
                                >
                                    <Icon
                                        icon="trash"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Delete</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </transition>
        </div>
        <Teleport to="body">
            <ConfirmDialag
                :show="showDeleteConfirmation"
                title="Delete redemption"
                :subtitle="deleteRedemptionSubtitle"
                :dialog="deleteDialogText"
                icon="trash"
                iconColor="red"
                :buttons="[
                    {
                        type: 'cancel',
                        text: 'Cancel',
                    },
                    {
                        type: 'confirm',
                        text: 'Delete redemption',
                        value: true,
                        classes: '!bg-red-500 !text-white hover:!bg-red-600',
                    },
                ]"
                @cancel="showDeleteConfirmation = false"
                @confirm="deleteRedemption"
            />
        </Teleport>
    </div>
</template>
<script setup>
import { useTripsStore } from '@/store/trips';
import { ref, computed } from 'vue';
import { useFloating, flip, offset, shift } from '@floating-ui/vue';
import { storeToRefs } from 'pinia';
import dayjs from 'dayjs';

import ConfirmDialag from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    redemption: {
        type: Object,
    },
    showLabel: {
        type: Boolean,
        default: true,
    },
    showShare: {
        type: Boolean,
        default: true,
    },
    showBorder: {
        type: Boolean,
        default: true,
    },
});

const tripsStore = useTripsStore();
const { trip } = storeToRefs(tripsStore);
const showMore = ref(false);
const showDeleteConfirmation = ref(false);
const buttonRef = ref(null);
const popoverRef = ref(null);
const { floatingStyles } = useFloating(buttonRef, popoverRef, {
    placement: 'bottom-end',
    middleware: [offset(10), flip(), shift(5)],
});

const getCityName = (airport) => {
    if (!airport) return '';
    const match = airport.match(/^([^,(-]+)/);
    return match ? match[1].trim() : airport;
};

const formatDateRange = (start, end) => {
    if (!start) return '';
    const startDate = dayjs(start);
    if (!end || start === end) {
        return startDate.format('MMM D, YYYY');
    }
    const endDate = dayjs(end);
    if (startDate.year() === endDate.year()) {
        return `${startDate.format('MMM D')} – ${endDate.format('MMM D, YYYY')}`;
    }
    return `${startDate.format('MMM D, YYYY')} – ${endDate.format('MMM D, YYYY')}`;
};

const redemptionDetails = computed(() => {
    if (!props.redemption) return '';
    
    const type = props.redemption.redemption_type?.slug;
    let details = '';
    
    if (type === 'hotel') {
        const hotelName = props.redemption.name || '';
        const dateRange = formatDateRange(props.redemption.start_date, props.redemption.end_date);
        details = hotelName ? `${hotelName}${dateRange ? `, ${dateRange}` : ''}` : '';
    } else if (type === 'flight') {
        const segments = props.redemption.travel_segments;
        if (segments && segments.length > 0) {
            const segment = segments[0];
            if (segment.origin && segment.destination) {
                const originCity = getCityName(segment.origin_name || segment.origin);
                const destinationCity = getCityName(segment.destination_name || segment.destination);
                const dateRange = formatDateRange(
                    segment.departure_date || props.redemption.start_date,
                    segment.arrival_date || props.redemption.end_date
                );
                details = `Flight ${originCity} - ${destinationCity}${dateRange ? `, ${dateRange}` : ''}`;
            }
        }
    } else if (type === 'train') {
        const segments = props.redemption.travel_segments;
        if (segments && segments.length > 0) {
            const segment = segments[0];
            if (segment.origin && segment.destination) {
                const originCity = getCityName(segment.origin_name || segment.origin);
                const destinationCity = getCityName(segment.destination_name || segment.destination);
                const dateRange = formatDateRange(
                    segment.departure_date || props.redemption.start_date,
                    segment.arrival_date || props.redemption.end_date
                );
                details = `Train ${originCity} – ${destinationCity}${dateRange ? `, ${dateRange}` : ''}`;
            }
        }
    } else if (type === 'rental-car') {
        const pickup = props.redemption.pickup_location || '';
        const dropoff = props.redemption.dropoff_location || '';
        const dateRange = formatDateRange(props.redemption.start_date, props.redemption.end_date);
        details = `Rental car ${pickup} – ${dropoff}${dateRange ? `, ${dateRange}` : ''}`;
    } else if (type === 'other') {
        const name = props.redemption.name || '';
        const dateRange = formatDateRange(props.redemption.start_date, props.redemption.end_date);
        details = name ? `${name}${dateRange ? `, ${dateRange}` : ''}` : '';
    }
    
    return details;
});

const deleteRedemptionSubtitle = computed(() => {
    return redemptionDetails.value;
});

const deleteDialogText = computed(() => {
    return 'You are about to delete this redemption. This action cannot be undone.';
});

const showRedemption = () => {
    tripsStore.setActiveRedemption(props.redemption);
    tripsStore.setPanelRedemption(props.redemption);
    tripsStore.setShowRedemptionPanel(true);
};

const editRedemption = () => {
    tripsStore.setActiveRedemption(props.redemption);
    tripsStore.setShowRedemptionEditModal(true);
};

const deleteRedemption = () => {
    tripsStore.deleteRedemption(props.redemption.uuid);
};
</script>
